import type { ButtonHTMLAttributes, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export type GfgButtonVariant =
  | "primary" // rodeo black fill — main CTA / confirm
  | "accent" // dark dust fill — the single accent-filled action
  | "outline" // dust border, fills light-dust on hover — the everyday workhorse
  | "secondary" // white + smoke border — cancel / back
  | "danger" // wine fill — destructive
  | "text"; // no container — inline actions

export type GfgButtonTone = "accent" | "destructive"; // only used by variant="text"

export interface GfgButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: GfgButtonVariant;
  /** Colour role for variant="text": accent (dark dust) or destructive (wine). */
  tone?: GfgButtonTone;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

// NB: no border COLOR here on purpose. Each variant supplies its own border
// color (fills use `border-transparent`, outlined variants use a real color).
// Putting `border-transparent` in BASE made every variant carry BOTH it and
// its real border color, and in the built output.css `.border-transparent` is
// emitted after `.border-dark-dust`/`.border-smoke`, so the outlined variants'
// borders silently lost the cascade and rendered invisible.
const BASE =
  "inline-flex items-center justify-center gap-2 cursor-pointer rounded-[6px] border " +
  "font-lato font-bold uppercase text-[11px] tracking-[1.5px] " +
  "transition-colors disabled:opacity-40 disabled:cursor-default";

// Container sizing — applied to every variant EXCEPT "text", which is a bare
// inline action (no padding, so it aligns flush with surrounding content).
const CONTAINER_SIZE = "px-[20px] py-[10px] min-h-[40px]";

const VARIANT: Record<GfgButtonVariant, string> = {
  primary: "bg-rodeo-black border-transparent text-white hover:bg-[#1a1a1a]",
  accent: "bg-dark-dust border-dark-dust text-white tracking-[2px] hover:bg-[#9b7d63]",
  outline: "bg-transparent border-dark-dust text-rodeo-black tracking-[1px] hover:bg-light-dust",
  secondary: "bg-white border-smoke text-rodeo-black tracking-[1px] hover:bg-wax",
  danger: "bg-good-wine border-transparent text-white hover:bg-[#8f3c36]",
  text: "bg-transparent border-none normal-case tracking-normal text-[13px] font-semibold hover:opacity-80",
};

export function GfgButton({
  variant = "primary",
  tone = "accent",
  startIcon,
  endIcon,
  className = "",
  children,
  ...rest
}: GfgButtonProps) {
  const toneCls =
    variant === "text" ? (tone === "destructive" ? "text-good-wine" : "text-dark-dust") : "";
  const sizeCls = variant === "text" ? "" : CONTAINER_SIZE;
  return (
    <button
      // twMerge so caller `className` overrides win over BASE/size/variant on
      // conflicts (padding, radius, display, …) instead of both landing and the
      // cascade deciding by source order.
      className={twMerge(BASE, sizeCls, VARIANT[variant], toneCls, className)}
      {...rest}
    >
      {startIcon}
      {children}
      {endIcon}
    </button>
  );
}

export default GfgButton;
